import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import { setSelectedOffer } from '@/reducers/jbpGeneralSlice';
import { FormProvider, useForm } from 'react-hook-form';
import { FormDatePicker } from '@portal_components/components/FormDatePicker';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormButton } from '@portal_components/components/FormButton';
import { ExternalLink } from '@/components/ExternalLink';
import { GenericLoadingElement } from '@/components/GenericLoadingElement';
import { List } from '@/components/List';
import { ListItem, feedbackColour } from '@/components/ListItem';
import { Modal } from '@/components/Modal';
import { Title } from '@portal_components/components/Title';
import { Typography } from '@/components/Typography';
import { useDefinedParams } from '@/hooks/useDefinedParams';
import { useJbpQuery, RestTypes } from '@/hooks/useJbpQuery';
import { getServicePowerMappings } from '@/utils/getServicePowerMappings';
import { getOfferServicePowerErrorResponse, getOffersSuccessType, Offer } from '../../../../../APIDocs/apiTypes';
import { ApiError } from '../../ApiError';
import { Button } from '@/components/Button';

type BookJobOffersFormValues = {
  preferredDate: string;
};

export const BookJobOffers = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { site, jobDetails } = useAppSelector(state => state.jbp);
  const showMoreIncrement = 7;
  const [showMoreCount, setShowMoreCount] = useState(showMoreIncrement);
  const [spErrorCode, setSpErrorCode] = useState<string | null>(null);

  const [mpan, serviceLine] = useDefinedParams(['mpan', 'serviceLine']);

  useEffect(() => {
    setSelectedOffer(undefined);
  }, []);

  //Modal controls
  const [showConfirmWarrantOfferSelection, setShowConfirmWarrantOfferSelection] = useState(false);
  const [showDateErrorModal, setShowDateErrorModal] = useState(false);

  const postCode = jobDetails?.siteAddress.postCode ?? site?.siteAddress.postCode;

  const defaultAllDayTimeBandStart = '0800';
  const defaultAllDayTimeBandEnd = '1700';

  const defaultPreferredDate = DateTime.now().toFormat('yyyy-MM-dd');

  const [queryData, setQueryDataForGetOffers] = useState({
    mpxn: mpan,
    jobType: jobDetails?.jobTypeCode,
    fuel: 'E',
    siteMpid: site?.userMpid ?? '',
    postcode: postCode,
    startDate: site?.offerDateRange.startDate,
    endDate: site?.offerDateRange.endDate,
    timeBandStart: defaultAllDayTimeBandStart,
    timeBandEnd: defaultAllDayTimeBandEnd,
    force: false,
    promiseSetId: '',
    preferredDate: defaultPreferredDate
  });

  const onGetOffersError = (err: getOfferServicePowerErrorResponse) => {
    console.log('Error returned from service power:' + err);
    setSpErrorCode(err?.message ? `Error received from Service Power: ${err.message}` : getServicePowerMappings(err?.errorCode));
  };

  const {
    isLoading,
    data,
    refetch: refetchOffers
  } = useJbpQuery<getOffersSuccessType, getOfferServicePowerErrorResponse>(RestTypes.Post, queryData, ['offersExternal'], `jbp/offers/request`, true, serviceLine, undefined, onGetOffersError);

  useEffect(() => {
    refetchOffers();
  }, [queryData, refetchOffers]);

  const onShowMoreClick = () => {
    setShowMoreCount(showMoreCount + showMoreIncrement);
  };

  const listItemClicked = (offer: Offer) => {
    dispatch(setSelectedOffer({ offer, force: false }));

    if (jobDetails?.isWarrantJob) {
      setShowConfirmWarrantOfferSelection(true);
    } else {
      navigate(`/bookJob/communications/${mpan}/${serviceLine}`);
    }
  };

  const handleBookJobSelection = () => {
    navigate(`/bookingResult/${mpan}/${serviceLine}`);
  };

  const offersToDisplay = data != undefined ? data.filter((offer, index) => index < showMoreCount) : [];

  const validationSchema = Yup.object().shape({
    preferredDate: Yup.string()
      .required('Date is required')
      .test('is-valid-date', 'Invalid date', (date: string) => {
        const selectedDate = DateTime.fromISO(date);
        const currentDate = DateTime.now().startOf('day');
        const startDate = site?.offerDateRange.startDate;
        const offerDateRangeStart = startDate != null ? DateTime.fromFormat(startDate, 'yyyy-MM-dd').startOf('day') : currentDate;

        const endDate = site?.offerDateRange.endDate;
        const offerDateRangeEnd = endDate != undefined ? DateTime.fromFormat(endDate, 'yyyy-MM-dd').endOf('day') : null;

        const newSelectedDate = selectedDate?.startOf('day');

        if (newSelectedDate && date) {
          const isSelectedDateWithinOfferDateRange = newSelectedDate >= offerDateRangeStart && (offerDateRangeEnd === null || newSelectedDate <= offerDateRangeEnd);
          const isSelectedDateInFuture = newSelectedDate >= currentDate;
          if (!isSelectedDateInFuture || !isSelectedDateWithinOfferDateRange) {
            setShowDateErrorModal(true);
            return false;
          }
          return true;
        }
      })
  });

  const methods = useForm<BookJobOffersFormValues>({
    defaultValues: {
      preferredDate: defaultPreferredDate
    },
    resolver: yupResolver(validationSchema as any)
  });

  const handleSubmit = (data: BookJobOffersFormValues) => {
    const formattedDate = DateTime.fromISO(data.preferredDate).toFormat('yyyy-MM-dd');
    // This is used by warrant jobs only and sets startDate rather than preferredDate
    setQueryDataForGetOffers(prevState => ({
      ...prevState,
      startDate: formattedDate
    }));
  };

  const [preferredDate] = methods.watch(['preferredDate']);

  const formattedPreferredDate = preferredDate ? DateTime.fromISO(preferredDate).toFormat('yyyy-MM-dd') : '';

  const title = 'Your appointment offers';
  const isButtonDisabled = () => data != undefined && data.length <= showMoreCount;

  const showWarrantInfo = () => {
    return jobDetails?.isWarrantJob && spErrorCode === null;
  };

  return (
    <div className="mx-10 mb-12">
      <Title text={title} className="mt-7 mb-4"></Title>

      {showWarrantInfo() === true ? (
        <div>
          <Typography
            content={`Please select a date for the Warrant Job. We have initially provided a list of offers. If you require a different date, please use the date picker.`}
            className="mb-3"
          />
          <Typography className="inline-block mr-1" content={`If your preferred date is not available, please contact Stark to arrange the appointment using the following link:`} />
          <ExternalLink className="inline-block" link="https://queryportal.siemensmetering.co.uk" linkText="Stark Query Portal" />
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
              <div className="grid grid-cols-2 gap-2">
                <FormDatePicker title="Select Date For Warrant Job" name="preferredDate" mandatory />
                <div className="pl-2 pt-2 pb-4 mb-2">
                  <FormButton primary={true} disabled={isLoading} type="submit">
                    Search
                  </FormButton>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      ) : (
        <span></span>
      )}
      {isLoading ? (
        <GenericLoadingElement count={showMoreIncrement} height="h-16" className="jbp-offers-loading w-4/6"></GenericLoadingElement>
      ) : offersToDisplay != undefined && offersToDisplay.length > 0 ? (
        <div>
          <List title={title} data={offersToDisplay}>
            {offersToDisplay.map((offer, index) => {
              return (
                <ListItem
                  title="Appointment"
                  className="mr-10 w-4/6"
                  cells={[`${offer.ScheduledDate}`]}
                  feedbackColour={feedbackColour.green}
                  key={index}
                  tagText="Available"
                  onClick={() => listItemClicked(offer)}
                />
              );
            })}
          </List>
          <Button text="Show more" primary={true} disabled={isButtonDisabled()} onClick={onShowMoreClick} className="mt-2" />
        </div>
      ) : (
        <ApiError errorDescription={spErrorCode ?? ''}></ApiError>
      )}
      {showDateErrorModal ? (
        <Modal title="Error">
          <Typography content={`The date you have selected is in the past and/or is not within your assigned appointment range.`} className="mb-3" />
          <Typography content={`Please select another date or contact Stark to arrange the appointment.`} className="mb-3" />
          <Button primary={true} text="OK" className="mr-4 mt-5" disabled={false} onClick={() => setShowDateErrorModal(false)} />
        </Modal>
      ) : (
        <span></span>
      )}
      {showConfirmWarrantOfferSelection ? (
        <Modal title="Continue with Job Booking?">
          <Typography content={`You have selected: ${formattedPreferredDate}.`} className="mb-3" />
          <Typography content={`If this is correct click Confirm to complete the booking.`} className="mb-3" />
          <Button primary={false} text="Cancel" className="mr-4 mt-5" disabled={false} onClick={() => setShowConfirmWarrantOfferSelection(false)} />
          <Button primary={true} text="Confirm" className="mr-4 mt-5" disabled={false} onClick={handleBookJobSelection} />
        </Modal>
      ) : (
        <span></span>
      )}
    </div>
  );
};
